"use client";

import { useState } from "react";
import {
  Calendar,
  Clock,
  Code,
  Database,
  Globe,
  GraduationCap,
  Server,
  User,
} from "lucide-react";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Footer from "../Header/Footer";
import Navbar from "../Header/Navbar";
import ApplyButton from "../common/ApplyButton";

const BscsPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [showAllSemesters, setShowAllSemesters] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <div className="font-sans">
      {/* Header */}
      <Navbar />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-800 to-blue-600 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-8 md:mb-0">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
                Bachelor of Science in Computer Science
              </h1>
              <p className="text-xl mb-6 text-blue-100">
                Develop cutting-edge skills for the digital future
              </p>
              {/* Apply Button */}
              <div className="flex flex-col md:flex-row gap-4 mt-8">
                <ApplyButton
                  to="/login"
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition duration-300 shadow-lg transform hover:scale-105"
                >
                  Apply Now
                </ApplyButton>
              </div>
            </div>
            <div
              className="md:w-1/2"
              style={{ height: "30rem", width: "43rem" }}
            >
              <img
                src="https://images.unsplash.com/photo-1610563166150-b34df4f3bcd6?q=80&w=1976&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                alt="BSCS Students"
                className="rounded-lg shadow-2xl transform hover:scale-105 transition duration-300"
                style={{ objectFit: "cover", height: "100%", width: "100%" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Quick Info */}
      <section className="bg-gray-100 py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-lg transform hover:scale-105 transition duration-300">
              <Calendar className="text-blue-600 mb-4" size={48} />
              <h3 className="font-semibold text-gray-800 text-xl mb-2">
                Program Duration
              </h3>
              <p className="text-gray-600">4 Years (8 Semesters)</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg transform hover:scale-105 transition duration-300">
              <GraduationCap className="text-blue-600 mb-4" size={48} />
              <h3 className="font-semibold text-gray-800 text-xl mb-2">
                Degree Awarded
              </h3>
              <p className="text-gray-600">BS in Computer Science</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg transform hover:scale-105 transition duration-300">
              <Clock className="text-blue-600 mb-4" size={48} />
              <h3 className="font-semibold text-gray-800 text-xl mb-2">
                Credit Hours
              </h3>
              <p className="text-gray-600">133 Credit Hours</p>
            </div>
          </div>
        </div>
      </section>

      {/* Program Tabs */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-center text-gray-800 mb-4">
              Program Details
            </h2>
            <p className="text-center text-gray-600 max-w-5xl mx-auto text-lg">
              The Bachelor of Science in Computer Science (BSCS) at NIIT,
              Lahore, is a 4-year HEC-recognized program comprising 130 credit
              hours spread over 8 semesters. The program equips students with
              strong foundations in computing, programming, data structures, and
              system design. It follows a rigorous curriculum aligned with
              national standards and global industry needs. Students also engage
              in hands-on labs, industry projects, and a final-year capstone
              project to enhance practical learning.{" "}
            </p>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap justify-center border-b border-gray-200 mb-8">
            {["overview", "curriculum", "careers"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-3 px-6 font-medium text-sm focus:outline-none transition duration-300 ${
                  activeTab === tab
                    ? "border-b-2 border-blue-600 text-blue-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="bg-white p-8 rounded-lg shadow-lg">
            {activeTab === "overview" && (
              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  Program Overview
                </h3>
                <p className="text-gray-700 mb-6">
                  The BSCS program is designed to develop core competencies in
                  software development, algorithmic thinking, and system-level
                  problem solving. It balances theoretical knowledge with
                  practical experience to prepare students for successful
                  careers in the technology sector. The curriculum is enriched
                  with modern tools, lab-based learning, and industry engagement
                  to foster innovation and technical excellence.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {[
                    {
                      icon: <Code className="text-blue-600" size={36} />,
                      title: "Modern Curriculum",
                      description:
                        "The curriculum is regularly reviewed and updated to reflect the latest industry practices, programming languages, and emerging technologies.",
                    },
                    {
                      icon: <User className="text-blue-600" size={36} />,
                      title: "Expert Faculty",
                      description:
                        "Courses are taught by highly qualified faculty with experience in academia, industry, and applied research in computing domains.",
                    },
                    {
                      icon: <Server className="text-blue-600" size={36} />,
                      title: "State-of-the-art Labs",
                      description:
                        "Students have access to advanced computing labs equipped with the latest hardware, development platforms, and specialized software tools.",
                    },
                    {
                      icon: <Globe className="text-blue-600" size={36} />,
                      title: "Industry Connections",
                      description:
                        "Strong industry linkages provide students with internship opportunities, guest lectures, and collaborative projects for real-world exposure.",
                    },
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="flex items-start bg-gray-50 p-6 rounded-lg transform hover:scale-105 transition duration-300"
                    >
                      <div className="bg-white p-3 rounded-full mr-4 shadow-md">
                        {item.icon}
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-800 text-lg mb-2">
                          {item.title}
                        </h4>
                        <p className="text-gray-600">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "curriculum" && (
              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  Curriculum
                </h3>
                <p className="text-gray-700 mb-6">
                  Our comprehensive curriculum is designed to build a strong
                  foundation in computer science while offering specialization
                  opportunities in emerging technologies. This curriculum is
                  applicable from Fall 2025 intake onwards.
                </p>

                <div className="mb-8">
                  <h4 className="text-xl font-semibold text-gray-800 mb-4">
                    Course Structure
                  </h4>

                  {/* Semester Data */}
                  {[
                    // Semester 1
                    {
                      semester: 1,
                      courses: [
                        {
                          sNo: 1,
                          code: "CS111",
                          title: "Programming Fundamentals",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 2,
                          code: "CS111L",
                          title: "Programming Fundamentals Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 3,
                          code: "CS181",
                          title:
                            "Application of Information and Communication Technologies",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 4,
                          code: "CS181L",
                          title:
                            "Application of Information and Communication Technologies Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 5,
                          code: "HU405",
                          title: "Ideology and Constitution of Pakistan",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 6,
                          code: "EL170",
                          title: "Functional English",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 7,
                          code: "PH111",
                          title: "Applied Physics",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 8,
                          code: "PH111L",
                          title: "Applied Physics Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 9,
                          code: "MA114",
                          title: "Foundational Mathematics (Non-Credit)",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                      ],
                      totalTheory: 12,
                      totalLab: 3,
                      totalCredits: 15,
                    },
                    // Semester 2
                    {
                      semester: 2,
                      courses: [
                        {
                          sNo: 1,
                          code: "CS112",
                          title: "Object Oriented Programming",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 2,
                          code: "CS112L",
                          title: "Object Oriented Programming Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 3,
                          code: "CS130",
                          title: "Database Systems",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 4,
                          code: "CS130L",
                          title: "Database Systems Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 5,
                          code: "EE123",
                          title: "Digital Logic Design",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 6,
                          code: "EE123L",
                          title: "Digital Logic Design Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 7,
                          code: "MA216",
                          title: "Discrete Structures",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 8,
                          code: "EL271",
                          title: "Expository Writing",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 9,
                          code: "MA118",
                          title: "Pre-Calculus (Non-Credit)",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                      ],
                      totalTheory: 14,
                      totalLab: 3,
                      totalCredits: 17,
                    },
                    // Semester 3
                    {
                      semester: 3,
                      courses: [
                        {
                          sNo: 1,
                          code: "CS216",
                          title: "Data Structures",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 2,
                          code: "CS216L",
                          title: "Data Structures Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 3,
                          code: "CS215",
                          title: "Information Security",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 4,
                          code: "CS215L",
                          title: "Information Security Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 5,
                          code: "MA110",
                          title: "Calculus and Analytic Geometry",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 6,
                          code: "MA301",
                          title: "Probability and Statistics",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 7,
                          code: "CSXXX",
                          title: "Domain Elective-I",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 8,
                          code: "CSXXXL",
                          title: "Domain Elective-I Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                      ],
                      totalTheory: 13,
                      totalLab: 3,
                      totalCredits: 16,
                    },
                    // Semester 4
                    {
                      semester: 4,
                      courses: [
                        {
                          sNo: 1,
                          code: "CS226",
                          title: "Computer Organization and Assembly Language",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 2,
                          code: "CS226L",
                          title:
                            "Computer Organization and Assembly Language Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 3,
                          code: "HU411",
                          title: "Civics and Community Engagement",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 4,
                          code: "CS305",
                          title: "Software Engineering",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 5,
                          code: "CSXXX",
                          title: "Domain Elective-II",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 6,
                          code: "CSXXXL",
                          title: "Domain Elective-II Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 7,
                          code: "CS260",
                          title: "Computer Networks",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 8,
                          code: "CS260L",
                          title: "Computer Networks Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 9,
                          code: "MA105",
                          title: "Multivariable Calculus",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                      ],
                      totalTheory: 14,
                      totalLab: 3,
                      totalCredits: 17,
                    },
                    // Semester 5
                    {
                      semester: 5,
                      courses: [
                        {
                          sNo: 1,
                          code: "CS325",
                          title: "Operating Systems",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 2,
                          code: "CS325L",
                          title: "Operating Systems Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 3,
                          code: "CS332",
                          title: "Design and Analysis of Algorithms",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 4,
                          code: "CS344",
                          title: "Artificial Intelligence",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 5,
                          code: "CS344L",
                          title: "Artificial Intelligence Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 6,
                          code: "CSXXX",
                          title: "Domain Elective-III",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 7,
                          code: "CSXXXL",
                          title: "Domain Elective-III Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 8,
                          code: "BA152",
                          title: "Introduction to Management",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 9,
                          code: "MA201",
                          title: "Linear Algebra",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                      ],
                      totalTheory: 14,
                      totalLab: 3,
                      totalCredits: 17,
                    },
                    // Semester 6
                    {
                      semester: 6,
                      courses: [
                        {
                          sNo: 1,
                          code: "EL400",
                          title: "Technical and Business Writing",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 2,
                          code: "CS333",
                          title: "Theory of Automata",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 3,
                          code: "HU124",
                          title: "Islamic Studies and Ethics",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 4,
                          code: "CS330",
                          title: "Advanced Database Management Systems",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 5,
                          code: "CS330L",
                          title: "Advanced Database Management Systems Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 6,
                          code: "CSXXX",
                          title: "Domain Elective-IV",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 7,
                          code: "CSXXXL",
                          title: "Domain Elective-IV Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 8,
                          code: "CSXXX",
                          title: "Domain Elective-V",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 9,
                          code: "CSXXXL",
                          title: "Domain Elective-V Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 10,
                          code: "CS491",
                          title: "Final Year Project - I",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                      ],
                      totalTheory: 14,
                      totalLab: 4,
                      totalCredits: 18,
                    },
                    // Semester 7
                    {
                      semester: 7,
                      courses: [
                        {
                          sNo: 1,
                          code: "CS470",
                          title: "Professional Practices",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 2,
                          code: "CSXXX",
                          title: "Domain Elective-VI",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 3,
                          code: "CSXXX",
                          title: "Domain Elective-VI Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 4,
                          code: "CSXXX",
                          title: "Domain Elective-VII",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 5,
                          code: "CSXXX",
                          title: "Domain Elective-VII Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 6,
                          code: "CS426",
                          title: "Parallel and Distributed Computing",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 7,
                          code: "CS426L",
                          title: "Parallel and Distributed Computing Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 8,
                          code: "MK101",
                          title: "Principles of Marketing",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 9,
                          code: "CS492",
                          title: "Final Year Project - II",
                          theory: 0,
                          lab: 2,
                          total: 2,
                        },
                        {
                          sNo: 10,
                          code: "BA353",
                          title: "Entrepreneurship",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                      ],
                      totalTheory: 13,
                      totalLab: 5,
                      totalCredits: 18,
                    },
                    // Semester 8
                    {
                      semester: 8,
                      courses: [
                        {
                          sNo: 1,
                          code: "CS434",
                          title: "Compiler Construction",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 2,
                          code: "CS434L",
                          title: "Compiler Construction Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 3,
                          code: "CS424",
                          title: "Computer Architecture",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 4,
                          code: "CS424L",
                          title: "Computer Architecture Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 5,
                          code: "CS474",
                          title: "HCI & Computer Graphics",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 6,
                          code: "CS474L",
                          title: "HCI & Computer Graphics Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 7,
                          code: "CS493",
                          title: "Final Year Project-III",
                          theory: 0,
                          lab: 3,
                          total: 3,
                        },
                      ],
                      totalTheory: 6,
                      totalLab: 6,
                      totalCredits: 12,
                    },
                  ]
                    .filter(
                      (semester) => showAllSemesters || semester.semester <= 2
                    )
                    .map((semester) => (
                      <div key={semester.semester} className="mb-8">
                        <h5 className="text-lg font-semibold text-blue-700 mb-3">
                          Semester {semester.semester}
                        </h5>
                        <div className="overflow-x-auto">
                          <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                            <thead>
                              <tr className="bg-gray-100">
                                <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-b">
                                  S No
                                </th>
                                <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-b">
                                  Course Code
                                </th>
                                <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-b">
                                  Course Title
                                </th>
                                <th className="py-3 px-4 text-center text-sm font-medium text-gray-700 border-b">
                                  Theory
                                </th>
                                <th className="py-3 px-4 text-center text-sm font-medium text-gray-700 border-b">
                                  Lab
                                </th>
                                <th className="py-3 px-4 text-center text-sm font-medium text-gray-700 border-b">
                                  Total
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {semester.courses.map((course) => (
                                <tr
                                  key={course.sNo}
                                  className="hover:bg-gray-50"
                                >
                                  <td className="py-2 px-4 border-b text-sm">
                                    {course.sNo}
                                  </td>
                                  <td className="py-2 px-4 border-b text-sm">
                                    {course.code}
                                  </td>
                                  <td className="py-2 px-4 border-b text-sm">
                                    {course.title}
                                  </td>
                                  <td className="py-2 px-4 border-b text-sm text-center">
                                    {course.theory}
                                  </td>
                                  <td className="py-2 px-4 border-b text-sm text-center">
                                    {course.lab}
                                  </td>
                                  <td className="py-2 px-4 border-b text-sm text-center">
                                    {course.total}
                                  </td>
                                </tr>
                              ))}
                              <tr className="bg-blue-50 font-medium">
                                <td
                                  className="py-2 px-4 border-b text-sm"
                                  colSpan="3"
                                >
                                  Total Credit Hours
                                </td>
                                <td className="py-2 px-4 border-b text-sm text-center">
                                  {semester.totalTheory}
                                </td>
                                <td className="py-2 px-4 border-b text-sm text-center">
                                  {semester.totalLab}
                                </td>
                                <td className="py-2 px-4 border-b text-sm text-center">
                                  {semester.totalCredits}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}

                  {!showAllSemesters && (
                    <div className="flex justify-center mt-8">
                      <button
                        onClick={() => setShowAllSemesters(true)}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg transition duration-300 shadow-md"
                      >
                        Show More Semesters
                      </button>
                    </div>
                  )}

                  <div className="mt-8 bg-blue-50 p-6 rounded-lg shadow-md">
                    <h4 className="text-xl font-semibold text-gray-800 mb-4">
                      Program Summary
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h5 className="font-medium text-gray-800 mb-2">
                          Total Credit Hours
                        </h5>
                        <ul className="list-disc pl-5 space-y-1 text-gray-700">
                          <li>Theory Courses: 106 credit hours</li>
                          <li>Lab Courses: 27 credit hours</li>
                          <li>Total: 133 credit hours</li>
                        </ul>
                      </div>
                      <div>
                        <h5 className="font-medium text-gray-800 mb-2">
                          Course Distribution
                        </h5>
                        <ul className="list-disc pl-5 space-y-1 text-gray-700">
                          <li>Core Courses: 42 courses</li>
                          <li>Domain Elective Courses: 14 courses</li>
                          <li>General Education: 12 courses</li>
                        </ul>
                      </div>
                    </div>
                    <div className="mt-4 text-sm text-gray-600">
                      <p>
                        * 3 Credit hours internship (Student can apply for 6-8
                        weeks supervised internship course)
                      </p>
                      <p>* Applicable from Fall 2025 Intake & Onwards</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "admission" && (
              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  Admission Requirements
                </h3>
                <p className="text-gray-700 mb-6">
                  To be eligible for admission to the BSCS program, applicants
                  must meet the following requirements:
                </p>

                <div className="space-y-8">
                  <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                    <h4 className="text-xl font-semibold text-gray-800 mb-4">
                      Academic Requirements
                    </h4>
                    <ul className="list-disc pl-5 space-y-2 text-gray-700">
                      <li>
                        Higher Secondary School Certificate (HSSC) or equivalent
                      </li>
                      <li>Mathematics as a compulsory subject at HSSC level</li>
                      <li>Good academic standing throughout academic career</li>
                    </ul>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                    <h4 className="text-xl font-semibold text-gray-800 mb-4">
                      Entrance Test
                    </h4>
                    <ul className="list-disc pl-5 space-y-2 text-gray-700">
                      <li>Qualifying score in the NIIT Entrance Test</li>
                      <li>
                        Test covers Mathematics, English, and Logical Reasoning
                      </li>
                      <li>Sample test papers available on request</li>
                    </ul>
                  </div>

                  {/* <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                    <h4 className="text-xl font-semibold text-gray-800 mb-4">
                      Required Documents
                    </h4>
                    <ul className="list-disc pl-5 space-y-2 text-gray-700">
                      <li>Completed application form</li>
                      <li>
                        Attested copies of academic certificates and transcripts
                      </li>
                      <li>Copy of CNIC or B-Form</li>
                      <li>Four recent passport-sized photographs</li>
                      <li>
                        Character certificate from last attended institution
                      </li>
                    </ul>
                  </div> */}

                  <div className="bg-blue-50 p-6 rounded-lg shadow-md border-l-4 border-blue-500">
                    <h4 className="text-xl font-semibold text-gray-800 mb-4">
                      Application Process
                    </h4>
                    <ol className="list-decimal pl-5 space-y-2 text-gray-700">
                      <li>Submit online application through the NIIT portal</li>
                      <li>Pay application fee</li>
                      <li>Appear for entrance test on the scheduled date</li>
                      <li>
                        Shortlisted candidates will be called for interview
                      </li>
                      <li>
                        Final selection based on academic record, entrance test,
                        and interview performance
                      </li>
                    </ol>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "careers" && (
              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  Career Prospects
                </h3>
                <p className="text-gray-700 mb-6">
                  Graduates of our BSCS program are well-prepared for diverse
                  career opportunities in the technology sector. The program
                  equips students with the skills and knowledge needed to excel
                  in various roles.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                    <h4 className="text-xl font-semibold text-gray-800 mb-4">
                      Job Opportunities
                    </h4>
                    <ul className="space-y-4">
                      {[
                        {
                          icon: <Code size={20} />,
                          title: "Software Developer/Engineer",
                          description:
                            "Design, develop, and maintain software applications",
                        },
                        {
                          icon: <Database size={20} />,
                          title: "Database Administrator",
                          description: "Manage and optimize database systems",
                        },
                        {
                          icon: <Globe size={20} />,
                          title: "Web Developer",
                          description:
                            "Create and maintain websites and web applications",
                        },
                        {
                          icon: <Server size={20} />,
                          title: "Systems Analyst",
                          description:
                            "Analyze and improve IT systems and processes",
                        },
                      ].map((job, index) => (
                        <li key={index} className="flex items-start">
                          <div className="bg-blue-100 p-2 rounded-full mr-3 mt-1">
                            {job.icon}
                          </div>
                          <div>
                            <span className="font-medium text-gray-800">
                              {job.title}
                            </span>
                            <p className="text-sm text-gray-600">
                              {job.description}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                    <h4 className="text-xl font-semibold text-gray-800 mb-4">
                      Advanced Opportunities
                    </h4>
                    <ul className="space-y-4">
                      {[
                        {
                          icon: <Code size={20} />,
                          title: "AI/ML Engineer",
                          description:
                            "Develop machine learning models and AI solutions",
                        },
                        {
                          icon: <Database size={20} />,
                          title: "Data Scientist",
                          description: "Analyze and interpret complex data",
                        },
                        {
                          icon: <Globe size={20} />,
                          title: "DevOps Engineer",
                          description: "Implement and manage CI/CD pipelines",
                        },
                        {
                          icon: <Server size={20} />,
                          title: "Cybersecurity Specialist",
                          description:
                            "Protect systems and networks from threats",
                        },
                      ].map((job, index) => (
                        <li key={index} className="flex items-start">
                          <div className="bg-blue-100 p-2 rounded-full mr-3 mt-1">
                            {job.icon}
                          </div>
                          <div>
                            <span className="font-medium text-gray-800">
                              {job.title}
                            </span>
                            <p className="text-sm text-gray-600">
                              {job.description}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="bg-blue-50 p-6 rounded-lg shadow-md mb-8">
                  <h4 className="text-xl font-semibold text-gray-800 mb-4">
                    Industry Sectors
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      "Software Companies",
                      "IT Consulting",
                      "Banking & Finance",
                      "E-commerce",
                      "Healthcare",
                      "Education",
                      "Government",
                      "Startups",
                    ].map((sector, index) => (
                      <div
                        key={index}
                        className="bg-white p-3 rounded text-center shadow-sm"
                      >
                        <p className="font-medium text-gray-700">{sector}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-xl font-semibold text-gray-800 mb-4">
                    Further Education
                  </h4>
                  <p className="text-gray-700 mb-4">
                    BSCS graduates can pursue advanced degrees such as:
                  </p>
                  <ul className="list-disc pl-5 space-y-2 text-gray-700">
                    <li>Master of Science in Computer Science</li>
                    <li>Master of Science in Artificial Intelligence</li>
                    <li>Master of Science in Data Science</li>
                    <li>Master of Business Administration (MBA) in IT</li>
                    <li>PhD in Computer Science or related fields</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {/* <section className="py-20 bg-gradient-to-r from-blue-800 to-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Start Your Journey in Computer Science?
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Join our BSCS program and build the skills needed for a successful
            career in the technology industry.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <ApplyButton
              to="/login"
              className="bg-white text-blue-700 hover:bg-blue-50 font-bold py-3 px-8 rounded-lg transition duration-300 shadow-lg transform hover:scale-105"
            >
              Apply Now
            </ApplyButton>
          </div>
        </div>
      </section> */}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default BscsPage;
